import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

import { api } from "../services/api";

// Create the main context
const BloodTestDataContext = createContext();
export const useBloodTestData = () => useContext(BloodTestDataContext);

// Create a non-volatile context for components that don't need frequent updates
const BloodTestInfoContext = createContext();
export const useBloodTestInfo = () => useContext(BloodTestInfoContext);

/**
 * BloodTestDataProvider
 *
 * Optimized context provider that loads all patient blood test data once on login.
 * Uses indexes for O(1) lookups and memoization to prevent unnecessary re-renders.
 *
 * Data Structure:
 * - patients: Array of all patients
 * - patientIndex: Object keyed by patient_id for fast lookups
 * - bloodTests: Object keyed by patient_id -> array of blood tests
 * - testResults: Object keyed by blood_test_id -> array of test results
 * - testDefinitions: Array of all blood test definitions
 * - testDefinitionIndex: Object keyed by test_name for fast lookups
 * - categories: Array of unique categories from definitions
 * - subcategories: Object keyed by category -> array of subcategories
 */
export const BloodTestDataProvider = ({ children, userId }) => {
  // Core data state
  const [patients, setPatients] = useState([]);
  const [patientIndex, setPatientIndex] = useState({});
  const patientIndexRef = useRef({});

  const [bloodTests, setBloodTests] = useState({});
  const [testResults, setTestResults] = useState({});

  const [testDefinitions, setTestDefinitions] = useState([]);
  const [testDefinitionIndex, setTestDefinitionIndex] = useState({});
  const testDefinitionIndexRef = useRef({});

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});

  // Selected state
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingDefinitions, setLoadingDefinitions] = useState(true);

  // Error state
  const [error, setError] = useState(null);

  // Update refs when indexes change
  useEffect(() => {
    patientIndexRef.current = patientIndex;
  }, [patientIndex]);

  useEffect(() => {
    testDefinitionIndexRef.current = testDefinitionIndex;
  }, [testDefinitionIndex]);

  /**
   * Load test definitions once
   * These are static and shared across all users
   */
  const loadTestDefinitions = useCallback(async () => {
    try {
      setLoadingDefinitions(true);
      const response = await api.get('/api/blood-test-definitions');

      // Handle both formats: {success: true, definitions: []} and direct {definitions: []}
      const definitions = response.definitions || [];
      if (definitions.length > 0) {
        setTestDefinitions(definitions);

        // Build index for O(1) lookups by test_name
        const index = definitions.reduce((acc, def) => {
          acc[def.test_name] = def;
          // Also index by alternative names
          if (def.alternative_names) {
            def.alternative_names.forEach(altName => {
              acc[altName] = def;
            });
          }
          return acc;
        }, {});
        setTestDefinitionIndex(index);

        // Extract unique categories
        const uniqueCategories = [...new Set(definitions.map(d => d.category))].filter(Boolean).sort();
        setCategories(uniqueCategories);

        // Build subcategories by category
        const subCats = {};
        for (const def of definitions) {
          if (def.category && def.subcategory) {
            if (!subCats[def.category]) {
              subCats[def.category] = new Set();
            }
            subCats[def.category].add(def.subcategory);
          }
        }

        // Convert Sets to sorted arrays
        const subCatsObj = {};
        for (const [category, subCatSet] of Object.entries(subCats)) {
          subCatsObj[category] = [...subCatSet].filter(Boolean).sort();
        }
        setSubcategories(subCatsObj);

        setLoadingDefinitions(false);
      }
    } catch (err) {
      console.error('Error loading test definitions:', err);
      setError('Failed to load test definitions');
      setLoadingDefinitions(false);
    }
  }, []);

  /**
   * Load all patient data for the logged-in user
   * This is called once on mount when userId is available
   */
  const loadAllPatientData = useCallback(async () => {
    if (!userId) return;

    try {
      setLoadingPatients(true);

      // Fetch patients
      const patientsResponse = await api.get('/api/patients');

      // Handle direct array response
      const patientsList = Array.isArray(patientsResponse) ? patientsResponse : (patientsResponse.patients || []);
      setPatients(patientsList);

      console.log('[Context] Loaded patients:', patientsList.length);

      // Build patient index for O(1) lookups
      const pIndex = patientsList.reduce((acc, patient) => {
        acc[patient.id] = patient;
        return acc;
      }, {});
      setPatientIndex(pIndex);

      // Load blood tests for all patients in parallel
      const bloodTestsPromises = patientsList.map(patient =>
        api.get(`/api/patients/${patient.id}/tests`)
      );

      const bloodTestsResponses = await Promise.all(bloodTestsPromises);

      // Organize blood tests by patient_id
      const bloodTestsObj = {};
      const testResultsObj = {};

      for (let i = 0; i < patientsList.length; i++) {
        const patientId = patientsList[i].id;
        const response = bloodTestsResponses[i];

        // Handle direct array response
        const tests = Array.isArray(response) ? response : (response.tests || []);
        bloodTestsObj[patientId] = tests;

        console.log(`[Context] Patient ${patientId}: ${tests.length} blood tests`);

        // For each blood test, fetch its results
        // We'll do this in a second pass for efficiency
        for (const test of tests) {
          // Initialize empty results array
          testResultsObj[test.id] = [];
        }
      }

      setBloodTests(bloodTestsObj);

      // Now fetch all test results in parallel
      const allTests = Object.values(bloodTestsObj).flat();
      console.log('[Context] Fetching results for', allTests.length, 'blood tests');

      const testResultsPromises = allTests.map(test =>
        api.get(`/api/tests/results/${test.id}`)
      );

      const testResultsResponses = await Promise.all(testResultsPromises);

      for (let i = 0; i < allTests.length; i++) {
        const test = allTests[i];
        const response = testResultsResponses[i];

        // Handle direct array response
        const results = Array.isArray(response) ? response : (response.results || []);
        testResultsObj[test.id] = results;

        if (results.length > 0) {
          console.log(`[Context] Blood test ${test.id}: ${results.length} results`);
        }
      }

      setTestResults(testResultsObj);
      console.log('[Context] Total test results loaded:', Object.values(testResultsObj).flat().length);

      setLoadingPatients(false);

      // Auto-select first patient if available
      if (patientsList.length > 0) {
        setSelectedPatientId(patientsList[0].id);
        console.log('[Context] Auto-selected patient:', patientsList[0].id);
      }
    } catch (err) {
      console.error('Error loading patient data:', err);
      setError('Failed to load patient data');
      setLoadingPatients(false);
    }
  }, [userId]);

  /**
   * Initial data load
   */
  useEffect(() => {
    console.log('[BloodTestDataProvider] Mounted with userId:', userId);

    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([
        loadTestDefinitions(),
        loadAllPatientData()
      ]);
      setLoading(false);
    };

    loadAllData();

    return () => {
      console.log('[BloodTestDataProvider] Unmounted');
    };
  }, [userId, loadTestDefinitions, loadAllPatientData]);

  /**
   * Get blood tests for a specific patient
   */
  const getPatientBloodTests = useCallback((patientId) => {
    return bloodTests[patientId] || [];
  }, [bloodTests]);

  /**
   * Get test results for a specific blood test
   */
  const getTestResults = useCallback((bloodTestId) => {
    return testResults[bloodTestId] || [];
  }, [testResults]);

  /**
   * Get test results grouped by definition for a patient
   * Returns: { category: { subcategory: { test_name: [results] } } }
   */
  const getPatientResultsByCategory = useCallback((patientId) => {
    const tests = bloodTests[patientId] || [];
    const grouped = {};

    console.log('[Context] getPatientResultsByCategory for patient:', patientId);
    console.log('[Context] Found blood tests:', tests.length);

    for (const test of tests) {
      const results = testResults[test.id] || [];
      console.log(`[Context] Blood test ${test.id} has ${results.length} results`);

      for (const result of results) {
        const def = testDefinitionIndexRef.current[result.test_name];
        const category = def?.category || 'Other';
        const subcategory = def?.subcategory || 'General';
        // Use canonical test name from definition, not the original test_name from PDF
        const canonicalName = def?.test_name || result.test_name;

        if (!grouped[category]) grouped[category] = {};
        if (!grouped[category][subcategory]) grouped[category][subcategory] = {};
        if (!grouped[category][subcategory][canonicalName]) {
          grouped[category][subcategory][canonicalName] = [];
        }

        grouped[category][subcategory][canonicalName].push(result);
      }
    }

    console.log('[Context] Grouped results by category:', Object.keys(grouped));
    return grouped;
  }, [bloodTests, testResults]);

  /**
   * Get time series data for a specific test across all blood tests for a patient
   */
  const getTestTimeSeries = useCallback((patientId, testName) => {
    const tests = bloodTests[patientId] || [];
    const timeSeries = [];

    for (const test of tests) {
      const results = testResults[test.id] || [];
      // Find result by canonical test name (from definition), not original PDF name
      const result = results.find(r => {
        const def = testDefinitionIndexRef.current[r.test_name];
        const canonicalName = def?.test_name || r.test_name;
        return canonicalName === testName;
      });

      if (result && result.result_value !== null) {
        timeSeries.push({
          date: test.sample_date || test.sample_received || test.result_reported,
          value: result.result_value,
          unit: result.unit,
          isAbnormal: result.is_abnormal,
          normalRangeMin: result.normal_range_min,
          normalRangeMax: result.normal_range_max,
          normalRangeText: result.normal_range_text,
          bloodTestId: test.id,
          resultId: result.id
        });
      }
    }

    // Sort by date ascending
    timeSeries.sort((a, b) => new Date(a.date) - new Date(b.date));

    return timeSeries;
  }, [bloodTests, testResults]);

  /**
   * Get test definition by test name
   */
  const getTestDefinition = useCallback((testName) => {
    return testDefinitionIndexRef.current[testName] || null;
  }, []);

  /**
   * Get all tests in a category
   */
  const getTestsByCategory = useCallback((category) => {
    return testDefinitions.filter(def => def.category === category);
  }, [testDefinitions]);

  /**
   * Get all tests in a subcategory
   */
  const getTestsBySubcategory = useCallback((category, subcategory) => {
    return testDefinitions.filter(
      def => def.category === category && def.subcategory === subcategory
    );
  }, [testDefinitions]);

  /**
   * Refresh data for a specific patient
   */
  const refreshPatient = useCallback(async (patientId) => {
    try {
      const response = await api.get(`/api/patients/${patientId}/tests`);

      if (response.success) {
        // Update blood tests for this patient
        setBloodTests(prev => ({
          ...prev,
          [patientId]: response.tests || []
        }));

        // Fetch results for new tests
        const newTestResultsObj = {};
        for (const test of (response.tests || [])) {
          const resultsResponse = await api.get(`/api/tests/results/${test.id}`);
          if (resultsResponse.success) {
            newTestResultsObj[test.id] = resultsResponse.results || [];
          }
        }

        setTestResults(prev => ({
          ...prev,
          ...newTestResultsObj
        }));
      }
    } catch (err) {
      console.error('Error refreshing patient:', err);
    }
  }, []);

  /**
   * Add a new patient (after upload)
   */
  const addPatient = useCallback((patient) => {
    setPatients(prev => [...prev, patient]);
    setPatientIndex(prev => ({
      ...prev,
      [patient.id]: patient
    }));
    setBloodTests(prev => ({
      ...prev,
      [patient.id]: []
    }));
  }, []);

  // Memoize volatile context value (frequently updated)
  const bloodTestDataValue = useMemo(
    () => ({
      userId,
      selectedPatientId,
      setSelectedPatientId,
      patients,
      patientIndex,
      loading: loadingPatients,
      error,
      getPatientBloodTests,
      getTestResults,
      getTestTimeSeries,
      getPatientResultsByCategory,
      refreshPatient,
      addPatient,
    }),
    [
      userId,
      selectedPatientId,
      patients,
      patientIndex,
      loadingPatients,
      error,
      getPatientBloodTests,
      getTestResults,
      getTestTimeSeries,
      getPatientResultsByCategory,
      refreshPatient,
      addPatient,
    ]
  );

  // Memoize non-volatile context value (rarely updated)
  const bloodTestInfoValue = useMemo(
    () => ({
      testDefinitions,
      testDefinitionIndex,
      categories,
      subcategories,
      selectedCategory,
      setSelectedCategory,
      loading: loadingDefinitions,
      getTestDefinition,
      getTestsByCategory,
      getTestsBySubcategory,
    }),
    [
      testDefinitions,
      testDefinitionIndex,
      categories,
      subcategories,
      selectedCategory,
      loadingDefinitions,
      getTestDefinition,
      getTestsByCategory,
      getTestsBySubcategory,
    ]
  );

  return (
    <BloodTestDataContext.Provider value={bloodTestDataValue}>
      <BloodTestInfoContext.Provider value={bloodTestInfoValue}>
        {children}
      </BloodTestInfoContext.Provider>
    </BloodTestDataContext.Provider>
  );
};
